import { Button, Space, type TableColumnsType, Tooltip } from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { StyledButton } from "~Common/Components";
import PermissionCheck from "~Common/PermissionCheck";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import type { Person } from "~types/person";

const useGetPersonsTableColumns = (onDeletePerson: (personId: string, personName: string) => void) => {
  const { formatMessage } = useIntl();

  const columns: TableColumnsType<Person> = [
    {
      title: formatMessage({ id: "name.local.full" }),
      dataIndex: "nameFullLocal",
      key: "nameFullLocal",
    },
    {
      title: formatMessage({ id: "person.id" }),
      dataIndex: "personId",
      key: "personId",
    },
    {
      title: formatMessage({ id: "person.externalId" }),
      dataIndex: "externalId",
      key: "externalId",
    },
    {
      title: formatMessage({ id: "nameFullLatin" }),
      dataIndex: "nameFullLatin",
      key: "nameFullLatin",
    },
    {
      title: formatMessage({ id: "gender" }),
      dataIndex: "gender",
      key: "gender",
    },
    {
      title: formatMessage({ id: "nationality" }),
      dataIndex: "nationality",
      key: "nationality",
    },
    {
      title: formatMessage({ id: "date.of.birth" }),
      dataIndex: "dob",
      key: "dob",
      render: (value: string) => value?.split("-").reverse().join("/") || "",
    },
    {
      title: formatMessage({ id: "status" }),
      dataIndex: "status",
      key: "status",
    },
    {
      title: formatMessage({ id: "person.additionalDetails.height" }),
      dataIndex: ["additionalDetails", "height"],
      key: "additionalDetails.height",
    },
    {
      title: formatMessage({ id: "person.additionalDetails.weight" }),
      dataIndex: ["additionalDetails", "weight"],
      key: "additionalDetails.weight",
    },
    {
      title: formatMessage({ id: "person.additionalDetails.heightImperial" }),
      dataIndex: ["additionalDetails", "heightImperial"],
      key: "additionalDetails.heightImperial",
    },
    {
      title: formatMessage({ id: "person.additionalDetails.weightImperial" }),
      dataIndex: ["additionalDetails", "weightImperial"],
      key: "additionalDetails.weightImperial",
    },
    {
      title: formatMessage({ id: "person.additionalDetails.representation" }),
      dataIndex: ["additionalDetails", "representation"],
      key: "additionalDetails.representation",
    },
    {
      title: formatMessage({ id: "person.additionalDetails.juniorAssociationLeague" }),
      dataIndex: ["additionalDetails", "juniorAssociationLeague"],
      key: "additionalDetails.juniorAssociationLeague",
    },
    {
      title: formatMessage({ id: "person.additionalDetails.homeTown" }),
      dataIndex: ["additionalDetails", "homeTown"],
      key: "additionalDetails.homeTown",
    },
    {
      title: formatMessage({ id: "person.additionalDetails.college" }),
      dataIndex: ["additionalDetails", "college"],
      key: "additionalDetails.college",
    },
    {
      title: formatMessage({ id: "person.additionalDetails.collegeClass" }),
      dataIndex: ["additionalDetails", "collegeClass"],
      key: "additionalDetails.collegeClass",
    },
    {
      title: formatMessage({ id: "person.additionalDetails.school" }),
      dataIndex: ["additionalDetails", "school"],
      key: "additionalDetails.school",
    },
    {
      title: formatMessage({ id: "person.additionalDetails.schoolClass" }),
      dataIndex: ["additionalDetails", "schoolClass"],
      key: "additionalDetails.schoolClass",
    },
    {
      align: "center",
      width: 150,
      fixed: "right",
      render: (_, person: Person) => (
        <Space>
          <PermissionCheck permissionKeys={[PermissionNames.PERSON]} permissionOptions={[PermissionOperations.UPDATE]}>
            <StyledButton size="small" type="text">
              <Link to={`manage/${person.personId}`}>
                <FormattedMessage id="manage" />
              </Link>
            </StyledButton>
          </PermissionCheck>
          <PermissionCheck permissionKeys={[PermissionNames.PERSON]} permissionOptions={[PermissionOperations.DELETE]}>
            <Tooltip title={formatMessage({ id: "delete" })}>
              <Button
                onClick={() => onDeletePerson(person.personId, person.nameFullLocal)}
                type="text"
                size="small"
                icon={<i className="fas fa-trash-can mr-2" />}
              />
            </Tooltip>
          </PermissionCheck>
        </Space>
      ),
    },
  ];

  return columns;
};

export default useGetPersonsTableColumns;
